import { useEffect, useState } from 'react';

import { MarketingFooter, MarketingHeader } from '../components/MarketingChrome';
import { RequestAccessModal } from '../components/RequestAccessModal';

const inputSources = [
  { icon: 'bi-file-earmark-text', label: 'Manuals' },
  { icon: 'bi-clipboard-data', label: 'Maintenance logs' },
  { icon: 'bi-chat-left-text', label: 'Technician notes' },
  { icon: 'bi-file-earmark-bar-graph', label: 'Inspection reports' },
  { icon: 'bi-people', label: 'Tribal knowledge' },
];

const afterCapabilities = [
  { icon: 'bi-chat-dots', label: 'Ask with evidence' },
  { icon: 'bi-search', label: 'Investigate what persists' },
  { icon: 'bi-database-check', label: 'Preserve what is learned' },
];

function Route42LearningCore() {
  return (
    <div className="r42-how-core r42-how-core--journey" aria-label="Route42 learns the machine">
      <div className="r42-how-core-glow" aria-hidden="true" />
      <div className="r42-how-core-shape">
        <img className="r42-how-core-lockup" src="/brand/route42-lockup-white-text.png" width={1730} height={364} alt="Route42" />
      </div>
    </div>
  );
}

function JourneyArrow() {
  return (
    <div className="r42-how-journey-arrow" aria-hidden="true">
      <span />
      <i className="bi bi-arrow-right" />
    </div>
  );
}

function TransformationDiagram() {
  return (
    <div className="r42-how-journey" aria-label="Before Route42, Route42, and after Route42">
      <section className="r42-how-zone r42-how-zone--before">
        <span className="r42-how-zone-label">Before Route42</span>
        <h2>Machine knowledge is scattered.</h2>
        <p>History lives across documents, notes, systems, and people instead of around the machine itself.</p>

        <div className="r42-how-source-cloud" aria-label="Fragmented machine information">
          {inputSources.map((source) => (
            <span key={source.label}>
              <i className={`bi ${source.icon}`} aria-hidden="true" />
              {source.label}
            </span>
          ))}
        </div>

        <div className="r42-how-pain">
          <i className="bi bi-arrow-repeat" aria-hidden="true" />
          <span>
            <strong>Every issue starts from scratch.</strong>
            <small>Teams spend time reconstructing the machine&apos;s story before they can resolve the problem.</small>
          </span>
        </div>
      </section>

      <JourneyArrow />

      <section className="r42-how-zone r42-how-zone--route42">
        <span className="r42-how-zone-label">Route42</span>
        <h2>Route42 learns the machine.</h2>
        <p>Fragmented history becomes connected, evidence-grounded machine intelligence.</p>

        <Route42LearningCore />

        <div className="r42-how-route42-principles" aria-label="Route42 intelligence principles">
          <span><i className="bi bi-diagram-3" aria-hidden="true" />Connect context</span>
          <span><i className="bi bi-link-45deg" aria-hidden="true" />Ground evidence</span>
          <span><i className="bi bi-memory" aria-hidden="true" />Build memory</span>
        </div>
      </section>

      <JourneyArrow />

      <section className="r42-how-zone r42-how-zone--after">
        <span className="r42-how-zone-label">After Route42</span>
        <h2>The machine becomes more knowable over time.</h2>
        <p>Teams stop rebuilding context and start working from a shared, persistent understanding.</p>

        <div className="r42-how-after-actions">
          {afterCapabilities.map((capability) => (
            <div key={capability.label}>
              <i className={`bi ${capability.icon}`} aria-hidden="true" />
              <span>{capability.label}</span>
            </div>
          ))}
        </div>

        <div className="r42-how-persistence" aria-label="Persistent machine intelligence">
          <div className="r42-how-persistence-orbit" aria-hidden="true">
            <span className="r42-how-orbit r42-how-orbit--one" />
            <span className="r42-how-orbit r42-how-orbit--two" />
            <span className="r42-how-orbit r42-how-orbit--three" />
            <span className="r42-how-persistent-machine"><i className="bi bi-cpu" /></span>
          </div>
          <span>
            <strong>Persistent machine intelligence</strong>
            <small>What the organization learns does not disappear when the conversation ends.</small>
          </span>
        </div>
      </section>
    </div>
  );
}

export function HowRoute42WorksPage() {
  const [requestOpen, setRequestOpen] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'How Route42 Works | Machine Intelligence';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <main className="r42-marketing-page r42-how-page">
      <MarketingHeader onRequestAccess={() => setRequestOpen(true)} />

      <section className="r42-how-hero">
        <div className="r42-how-layout">
          <div className="r42-how-main">
            <span className="r42-marketing-eyebrow">How Route42 Works</span>
            <h1>From scattered machine history to <em>persistent machine intelligence.</em></h1>
            <TransformationDiagram />
          </div>

          <aside className="r42-how-explainer">
            <div className="r42-how-explainer-inner">
              <h2>Route42 turns fragmented machine knowledge into persistent machine intelligence.</h2>
              <p>
                Most manufacturers don&apos;t struggle because they lack data. They struggle because the machine&apos;s story is scattered across documents, history, technicians, and time.
              </p>
              <p>
                Route42 learns from that fragmented context and turns it into something teams can question, investigate, and carry forward.
              </p>

              <div className="r42-how-impact" aria-label="Expected impact">
                <strong>Expected impact</strong>
                <div className="r42-how-impact-list">
                  <span>Faster understanding</span>
                  <span>Better decisions</span>
                  <span>Less repeated confusion</span>
                  <span>Stronger continuous improvement</span>
                </div>
              </div>

              <div className="r42-how-benefits">
                <div>
                  <i className="bi bi-journal-text" aria-hidden="true" />
                  <span><strong>Learn from machine history</strong><small>Route42 ingests and understands documents, logs, notes, and tribal knowledge.</small></span>
                </div>
                <div>
                  <i className="bi bi-chat-dots" aria-hidden="true" />
                  <span><strong>Answer with evidence</strong><small>Get grounded answers with clear citations so teams can trust what they act on.</small></span>
                </div>
                <div>
                  <i className="bi bi-database-check" aria-hidden="true" />
                  <span><strong>Preserve what the organization learns</strong><small>Investigations, findings, and machine understanding improve instead of disappearing.</small></span>
                </div>
              </div>

              <button className="r42-marketing-primary r42-how-request" type="button" onClick={() => setRequestOpen(true)}>
                Request access <i className="bi bi-arrow-right" aria-hidden="true" />
              </button>
              <p className="r42-how-invitation-note"><i className="bi bi-lock" aria-hidden="true" /> Early access by invitation</p>
            </div>
          </aside>
        </div>
      </section>

      <MarketingFooter />
      <RequestAccessModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </main>
  );
}
